/**
 * ChoiceCard — a choice among a few, as a card that is a button.
 * Portable copy from mju-ai-workbench/webview-ui/src/components/projects/wizard/shared.tsx.
 * Needs `.card-art` and `.choice-card` from mojoup-art.css. Swap the icon import
 * for whatever icon set the host app uses (any 16px SVG component works).
 */
import { Check } from '@gravity-ui/icons';
import { cloneElement, isValidElement, type ReactNode } from 'react';

export type CardTone = 'teal' | 'violet' | 'amber' | 'blue' | 'rose' | 'green' | 'slate';
const TONES: readonly CardTone[] = ['teal', 'violet', 'blue', 'amber', 'green', 'rose'];
const MOTIFS = ['rings', 'arc', 'dots'] as const;

/** A steady colour for a card nobody gave one: the same card is the same colour every time it is shown. */
export function toneFor(key: string | undefined): CardTone {
  if (!key) return 'slate';
  let h = 0;
  for (const ch of key) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return TONES[h % TONES.length];
}

/**
 * A choice among a few: a card that is a button.
 *
 * Built top to bottom — icon, title, a sentence, a status chip pinned to the
 * foot — because these sit three abreast in a modal, and the row layout they
 * had (icon, text and tick side by side) left the text a column ninety pixels
 * wide: "This / machine", and a description that wrapped two words at a time.
 * Now the title has the card's whole width and stays on one line, the
 * sentence reads as a sentence, and cards in a row are the same height with
 * their chips on one baseline.
 *
 * Each card has a tone and wears its own icon, large and faint, as the
 * artwork behind it (`.choice-card`, index.css), so three options read as
 * three different things rather than three grey boxes. The selected one is
 * outlined in the accent and ticked; colour is never the only signal.
 */
export function ChoiceCard({
  selected,
  onPress,
  icon,
  title,
  description,
  badge,
  trailing,
  disabled,
  testId,
  tone,
}: {
  selected: boolean;
  onPress: () => void;
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** A status chip ("ready", "needs setup"), pinned to the foot of the card. */
  badge?: ReactNode;
  trailing?: ReactNode;
  disabled?: boolean;
  testId?: string;
  /** The card's colour; defaults to a steady one derived from `testId`. */
  tone?: CardTone;
}) {
  const colour = tone ?? toneFor(testId);
  const motif = MOTIFS[(testId?.length ?? 0) % MOTIFS.length];
  // The backdrop is the card's own icon again, much larger: artwork that means something.
  const art = isValidElement<{ className?: string }>(icon) ? cloneElement(icon, { className: 'choice-card__art-icon' }) : null;
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      data-choice={testId}
      data-tone={colour}
      data-motif={motif}
      data-selected={selected}
      data-compact={icon ? undefined : true}
      data-pressable={disabled ? undefined : true}
      onClick={onPress}
      className="card-art choice-card"
    >
      {art ? (
        <span className="choice-card__art" aria-hidden="true">
          {art}
        </span>
      ) : null}
      <span className="choice-card__head">
        {icon ? <span className="card-art__icon choice-card__icon">{icon}</span> : <span />}
        <span className="choice-card__trailing">{trailing ?? (selected ? <span className="choice-card__tick"><Check className="size-3" aria-hidden="true" /></span> : null)}</span>
      </span>
      <span className="choice-card__title">{title}</span>
      {description ? <span className="choice-card__description">{description}</span> : null}
      {badge ? <span className="choice-card__foot">{badge}</span> : null}
    </button>
  );
}
